import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { usePlatformAPI } from "../PlatformProvider";
import {
    createDepartmentApi,
    CreateDepartmentBody,
    Department,
    InfoConfig,
    ListDepartmentsParams,
    UpdateDepartmentBody,
    UpdateInfoConfigBody,
} from "./departments";
import type { Result } from "../PlatformClient";
import type { APIError } from "../APIError";

function unwrap<T>(result: Result<T>): T {
    const [data, err] = result;
    if (err) throw err;
    return data as T;
}

export function useDepartmentApi() {
    const client = usePlatformAPI();
    return useMemo(() => createDepartmentApi(client), [client]);
}

export const departmentKeys = {
    all: ["departments"] as const,
    lists: () => [...departmentKeys.all, "list"] as const,
    list: (params: ListDepartmentsParams) => [...departmentKeys.lists(), params] as const,
    details: () => [...departmentKeys.all, "detail"] as const,
    detail: (id: string) => [...departmentKeys.details(), id] as const,
    userCount: (id: string) => [...departmentKeys.detail(id), "user-count"] as const,
    guardians: (id: string) => [...departmentKeys.detail(id), "guardians"] as const,
    infoConfig: (id: string) => [...departmentKeys.detail(id), "info-config"] as const,
};

export function useDepartments(params: ListDepartmentsParams) {
    const api = useDepartmentApi();
    return useQuery(
        departmentKeys.list(params),
        () => api.list(params).then(unwrap),
    );
}

export function useDepartment(id: string) {
    const api = useDepartmentApi();
    return useQuery(
        departmentKeys.detail(id),
        () => api.get(id).then(unwrap),
        { enabled: !!id },
    );
}

export function useDepartmentUserCount(id: string) {
    const api = useDepartmentApi();
    return useQuery(
        departmentKeys.userCount(id),
        () => api.userCount(id).then(unwrap),
        { enabled: !!id },
    );
}

export function useDepartmentGuardians(id: string) {
    const api = useDepartmentApi();
    return useQuery(
        departmentKeys.guardians(id),
        () => api.listGuardians(id).then(unwrap),
        { enabled: !!id },
    );
}

export function useDepartmentInfoConfig(id: string) {
    const api = useDepartmentApi();
    return useQuery(
        departmentKeys.infoConfig(id),
        () => api.getInfoConfig(id).then(unwrap),
        { enabled: !!id },
    );
}

export function useCreateDepartment() {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<Department, APIError, CreateDepartmentBody>(
        (body) => api.create(body).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.lists()) },
    );
}

export function useUpdateDepartment(id: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, UpdateDepartmentBody>(
        (body) => api.update(id, body).then(unwrap),
        {
            onSuccess: () => {
                qc.invalidateQueries(departmentKeys.lists());
                qc.invalidateQueries(departmentKeys.detail(id));
            },
        },
    );
}

export function useArchiveDepartment(id: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, void>(
        () => api.archive(id).then(unwrap),
        {
            onSuccess: () => {
                qc.invalidateQueries(departmentKeys.lists());
                qc.invalidateQueries(departmentKeys.detail(id));
            },
        },
    );
}

export function useUnarchiveDepartment(id: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, void>(
        () => api.unarchive(id).then(unwrap),
        {
            onSuccess: () => {
                qc.invalidateQueries(departmentKeys.lists());
                qc.invalidateQueries(departmentKeys.detail(id));
            },
        },
    );
}

export function useDeleteDepartment() {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, string>(
        (id) => api.delete(id).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.lists()) },
    );
}

export function useAddGuardian(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, string>(
        (userID) => api.addGuardian(departmentID, userID).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.guardians(departmentID)) },
    );
}

export function useRemoveGuardian(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, string>(
        (userID) => api.removeGuardian(departmentID, userID).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.guardians(departmentID)) },
    );
}

export function useUnassignUsers(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, void>(
        () => api.unassignUsers(departmentID).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.userCount(departmentID)) },
    );
}

export function useUpdateInfoConfig(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<InfoConfig, APIError, UpdateInfoConfigBody>(
        (body) => api.updateInfoConfig(departmentID, body).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.infoConfig(departmentID)) },
    );
}

export function useUploadInfoConfigFile(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<InfoConfig, APIError, { file: File; label?: string }>(
        ({ file, label }) => api.uploadInfoConfigFile(departmentID, file, label).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.infoConfig(departmentID)) },
    );
}

export function useDeleteInfoConfigFile(departmentID: string) {
    const api = useDepartmentApi();
    const qc = useQueryClient();
    return useMutation<void, APIError, string>(
        (fileID) => api.deleteInfoConfigFile(departmentID, fileID).then(unwrap),
        { onSuccess: () => qc.invalidateQueries(departmentKeys.infoConfig(departmentID)) },
    );
}
